import { type IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type ReactNode } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

import { pillColorClasses } from '../lib/colorSchemes';
import { cn } from '../lib/utils';

type Variant = 'filled' | 'subtle' | 'outline';
type Color = 'primary' | 'secondary' | 'destructive' | 'warning' | 'neutral';
type Size = 'sm' | 'md' | 'lg';

// ── Variants ──────────────────────────────────────────────────────────────────

const pillVariants = tv({
  slots: {
    root: 'inline-flex items-center gap-1 rounded-full font-medium border border-transparent',
    icon: 'shrink-0',
    removeBtn: [
      '-mr-0.5 ml-0.5 inline-flex shrink-0 items-center justify-center rounded-full p-0.5',
      'transition-colors hover:bg-black/10 dark:hover:bg-white/10',
      '[outline:none] focus-visible:ring-1 focus-visible:ring-current'
    ]
  },
  variants: {
    size: {
      sm: { root: 'px-2 py-0.5 text-xs', icon: 'h-3 w-3' },
      md: { root: 'px-2.5 py-1 text-sm', icon: 'h-3.5 w-3.5' },
      lg: { root: 'px-3 py-1.5 text-base', icon: 'h-4 w-4' }
    }
  },
  defaultVariants: { size: 'md' }
});

type PillVariants = VariantProps<typeof pillVariants>;

// ── Component ─────────────────────────────────────────────────────────────────

interface PillProps {
  variant?: Variant;
  color?: Color;
  size?: Size;
  icon?: IconDefinition;
  /** Renders a remove (×) button and calls this when clicked */
  onRemove?: () => void;
  /** Accessible name for the remove button, for lists where "Remove" alone is ambiguous */
  removeLabel?: string;
  children?: ReactNode;
  className?: string;
}

function Pill({
  variant = 'subtle',
  color = 'neutral',
  size = 'md',
  icon,
  onRemove,
  removeLabel = 'Remove',
  children,
  className
}: PillProps) {
  const { root, icon: iconCls, removeBtn } = pillVariants({ size });
  const colorClass = pillColorClasses[color][variant];

  return (
    <span className={cn(root(), colorClass, className)}>
      {icon && <FontAwesomeIcon icon={icon} className={iconCls()} />}
      {children}
      {onRemove && (
        <button type="button" onClick={onRemove} aria-label={removeLabel} className={removeBtn()}>
          <FontAwesomeIcon icon={faXmark} className="h-[0.65em] w-[0.65em]" />
        </button>
      )}
    </span>
  );
}

export { Pill, pillVariants };
export type { PillProps, PillVariants };
